import React, { useState, useEffect, useMemo } from 'react';
import '../../styles/UserManagement.css';
import loadingSpinner from '../../images/loading-spinner.svg';
import eventTracker from '../../services/EventTracker';
import EventLogs from '../../components/EventLogs';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    department: '',
    role: 'Negotiation Employee',
    status: 'active'
  });
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const [operationLoading, setOperationLoading] = useState({
    create: false,
    update: false,
    delete: false,
    reactivate: false,
    resetPassword: false
  });
  const [loadingUserId, setLoadingUserId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showExportModal, setShowExportModal] = useState(false);
  const [activeTab, setActiveTab] = useState('users');

  // NEW SETUP
  const SIGNUP_ENDPOINT = 'https://eosriieqys8a9fl.m.pipedream.net/api/UserSetup/CreateNewUser';
  const USERS_ENDPOINT = 'https://eolx2a8eamgidsc.m.pipedream.net/api/UserSetup/GetAllUsers';
  const UPDATEUSERS_ENDPOINT = 'https://eo5vu7hq9c87kw.m.pipedream.net/api/UserSetup/UpdateUser';
  const RESET_PASSWORD = 'https://eobw40svivznw2z.m.pipedream.net/api/UserSetup/ResetPassword';
  const DELETEUSER_ENDPOINT = 'https://eougt8vjgdt6vsq.m.pipedream.net/api/UserSetup/DeleteUser';
  
  // // OLD SETUP -- remove endpoint it is now unaccessible 18AUG2025
  // const SIGNUP_ENDPOINT = 'https://eoq0bb4qs1i57j9.m.pipedream.net';
  // const USERS_ENDPOINT = 'https://eoojol0jlqsd4lb.m.pipedream.net';
  // const UPDATEUSERS_ENDPOINT = 'https://eo8x9b7icjv9lcr.m.pipedream.net';
  // const RESET_PASSWORD = 'https://eocz7zooimot4vn.m.pipedream.net';
  // const DELETEUSER_ENDPOINT = 'https://eonkalg4yp68155.m.pipedream.net';

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setCurrentUser(parsedUser);

        if (parsedUser.role !== 'Super Admin') {
          setError('Access denied. Super Admin role required.');
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        setError('Authentication error. Please login again.');
        setLoading(false);
        return;
      }
    } else {
      setError('Not authenticated. Please login.');
      setLoading(false);
      return;
    }

    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(USERS_ENDPOINT, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      let usersData = [];
      if (Array.isArray(data)) {
        usersData = data;
      } else if (data.users && Array.isArray(data.users)) {
        usersData = data.users;
      } else if (data.data && Array.isArray(data.data)) {
        usersData = data.data;
      }

      const validUsers = usersData.filter(user => {
        if (!user || typeof user !== 'object') {
          return false;
        }

        return true;
      });

      setUsers(validUsers);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(`Failed to load users: ${error.message}`);
      setLoading(false);
    }
  };

  const departments = useMemo(() => {
    const depts = users
      .filter(user => user.department)
      .map(user => user.department)
      .filter((value, index, self) => self.indexOf(value) === index);
    return ['all', ...depts.sort()];
  }, [users]);

  const roles = useMemo(() => {
    const roleList = users
      .map(user => user.role || 'Negotiation Employee')
      .filter((value, index, self) => self.indexOf(value) === index);
    return ['all', ...roleList.sort()];
  }, [users]);

  const filteredAndSortedUsers = useMemo(() => {
    let filtered = [...users];

    if (searchTerm) {
      filtered = filtered.filter(user => {
        const searchLower = searchTerm.toLowerCase();
        return (
          user.firstName?.toLowerCase().includes(searchLower) ||
          user.lastName?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.userId?.toLowerCase().includes(searchLower) ||
          user.department?.toLowerCase().includes(searchLower)
        );
      });
    }

    if (departmentFilter !== 'all') {
      filtered = filtered.filter(user => user.department === departmentFilter);
    }

    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => (user.role || 'Negotiation Employee') === roleFilter);
    }

    if (dateRangeFilter.startDate || dateRangeFilter.endDate) {
      filtered = filtered.filter(user => {
        if (!user.createdAt) return false;
        const userDate = new Date(user.createdAt);

        const start = dateRangeFilter.startDate
          ? new Date(dateRangeFilter.startDate + 'T00:00:00.000Z')
          : new Date('1900-01-01');

        const end = dateRangeFilter.endDate
          ? new Date(dateRangeFilter.endDate + 'T23:59:59.999Z')
          : new Date('2100-12-31');

        return userDate >= start && userDate <= end;
      });
    }

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'name') {
          aValue = `${a.firstName} ${a.lastName}`;
          bValue = `${b.firstName} ${b.lastName}`;
        }

        if (sortConfig.key === 'lastLogin' || sortConfig.key === 'createdAt') {
          const aDate = aValue ? new Date(aValue) : null;
          const bDate = bValue ? new Date(bValue) : null;

          const aTime = aDate && !isNaN(aDate.getTime()) ? aDate.getTime() : 0;
          const bTime = bDate && !isNaN(bDate.getTime()) ? bDate.getTime() : 0;

          aValue = aTime;
          bValue = bTime;
        }

        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';

        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    } else {
      filtered.sort((a, b) => {
        if (a.status === 'deleted' && b.status !== 'deleted') return 1;
        if (a.status !== 'deleted' && b.status === 'deleted') return -1;
        return 0;
      });
    }

    return filtered;
  }, [users, searchTerm, departmentFilter, roleFilter, dateRangeFilter, sortConfig]);

  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredAndSortedUsers.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedUsers, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedUsers.length / rowsPerPage);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setDepartmentFilter('all');
    setRoleFilter('all');
    setDateRangeFilter({ startDate: '', endDate: '' });
    setSortConfig({ key: null, direction: 'asc' });
    setCurrentPage(1);
    eventTracker.trackButtonClick('reset_filters');
  };

  const handleExportUsers = (format) => {
    const exportData = filteredAndSortedUsers.map(user => ({
      'User ID': user.userId,
      'First Name': user.firstName || '',
      'Last Name': user.lastName || '',
      'Email': user.email || '',
      'Department': user.department || '',
      'Role': user.role || 'Negotiation Employee',
      'Status': user.status || 'active',
      'Created Date': user.createdAt && !isNaN(new Date(user.createdAt).getTime())
        ? new Date(user.createdAt).toLocaleDateString()
        : '-',
      'Last Login': user.lastLogin && !isNaN(new Date(user.lastLogin).getTime())
        ? new Date(user.lastLogin).toLocaleDateString()
        : '-'
    }));

    eventTracker.trackDataExport(format, exportData.length, {
      filters: {
        search: searchTerm,
        department: departmentFilter,
        role: roleFilter,
        dateRange: dateRangeFilter
      }
    });

    if (format === 'csv') {
      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map(row =>
          headers.map(header => {
            const value = row[header];
            return value.includes(',') ? `"${value.replace(/"/g, '""')}"` : value;
          }).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } else if (format === 'json') {
      const jsonContent = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    }

    setShowExportModal(false);
  };

  const handleQuickEditDepartment = async (userId, newDepartment) => {
    try {
      const user = users.find(u => u.userId === userId);
      const response = await fetch(UPDATEUSERS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId,
          updateType: 'details',
          firstName: user.firstName,
          lastName: user.lastName,
          department: newDepartment,
          role: user.role,
          status: user.status,
          updatedBy: currentUser?.email || 'admin'
        })
      });

      const data = await response.json();

      if (data.success) {
        setUsers(users.map(u =>
          u.userId === userId
            ? { ...u, department: newDepartment }
            : u
        ));
      } else {
        alert(data.error || 'Failed to update department');
      }
    } catch (error) {
      console.error('Error updating department:', error);
      alert('Network error. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'firstName' || name === 'lastName') {
      let cleanedValue = value
        .replace(/[\r\n\t]+/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/[^a-zA-Z\s\-']/g, '')
        .trimStart();

      cleanedValue = cleanedValue.replace(/\s{2,}/g, ' ');

      if (cleanedValue.length > 0) {
        cleanedValue = cleanedValue.charAt(0).toUpperCase() + cleanedValue.slice(1);
      }

      setFormData(prev => ({
        ...prev,
        [name]: cleanedValue
      }));
    } else if (name === 'department') {
      let cleanedValue = value
        .replace(/[\r\n\t]+/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/[^a-zA-Z0-9\s\-]/g, '')
        .trimStart();

      setFormData(prev => ({
        ...prev,
        [name]: cleanedValue
      }));
    } else if (name === 'email') {
      const lowerEmail = value.toLowerCase();
      setFormData(prev => ({
        ...prev,
        [name]: lowerEmail
      }));

      if (lowerEmail && !lowerEmail.endsWith('@pacificdebt.com')) {
        setEmailError('Email must be a @pacificdebt.com address');
      } else {
        setEmailError('');
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    if (name === 'firstName' || name === 'lastName' || name === 'department') {
      setFormData(prev => ({
        ...prev,
        [name]: value.trim()
      }));
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    const cleanedData = {
      ...formData,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      department: formData.department.trim(),
      email: formData.email.trim().toLowerCase()
    };

    if (!cleanedData.email || !cleanedData.password || !cleanedData.firstName || !cleanedData.lastName) {
      alert('Please fill in all required fields');
      return;
    }

    if (!cleanedData.email.endsWith('@pacificdebt.com')) {
      alert('Email must be a @pacificdebt.com address');
      return;
    }

    if (!/^[a-zA-Z\s\-']+$/.test(cleanedData.firstName) || !/^[a-zA-Z\s\-']+$/.test(cleanedData.lastName)) {
      alert('Names can only contain letters, spaces, hyphens, and apostrophes');
      return;
    }

    setOperationLoading(prev => ({ ...prev, create: true }));

    try {
      const response = await fetch(SIGNUP_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...cleanedData,
          action: 'signup',
          timestamp: new Date().toISOString()
        })
      });

      const data = await response.json();

      if (response.ok) {
        alert('User created successfully!');
        eventTracker.trackUserAction('create_user', {
          userId: data.userId,
          email: cleanedData.email,
          role: cleanedData.role
        });
        setShowCreateModal(false);
        setEmailError('');
        setFormData({
          email: '',
          password: '',
          firstName: '',
          lastName: '',
          department: '',
          role: 'Negotiation Employee',
          status: 'active'
        });
        fetchUsers();
      } else {
        alert(data.error || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Network error. Please try again.');
    } finally {
      setOperationLoading(prev => ({ ...prev, create: false }));
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '',
      firstName: user.firstName,
      lastName: user.lastName,
      department: user.department,
      role: user.role,
      status: user.status || 'active'
    });
    setShowEditModal(true);
    eventTracker.trackModalOpen('edit_user', { userId: user.userId });
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();

    const cleanedData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      department: formData.department.trim()
    };

    if (!cleanedData.firstName || !cleanedData.lastName) {
      alert('Please fill in all required fields');
      return;
    }

    if (!/^[a-zA-Z\s\-']+$/.test(cleanedData.firstName) || !/^[a-zA-Z\s\-']+$/.test(cleanedData.lastName)) {
      alert('Names can only contain letters, spaces, hyphens, and apostrophes');
      return;
    }

    setOperationLoading(prev => ({ ...prev, update: true }));

    try {
      const response = await fetch(UPDATEUSERS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: selectedUser.userId,
          updateType: 'details',
          firstName: cleanedData.firstName,
          lastName: cleanedData.lastName,
          department: cleanedData.department,
          role: formData.role,
          status: formData.status,
          updatedBy: currentUser?.email || 'admin'
        })
      });

      const data = await response.json();

      if (data.success) {
        setUsers(users.map(user =>
          user.userId === selectedUser.userId
            ? { ...user, ...data.user }
            : user
        ));

        eventTracker.trackUserAction('update_user', selectedUser, {
          updatedFields: Object.keys(cleanedData).concat(['role', 'status'])
        });

        alert('User updated successfully!');
        setShowEditModal(false);
        setSelectedUser(null);
      } else {
        alert(data.error || 'Failed to update user');
      }

    } catch (error) {
      console.error('Error updating user:', error);
      alert('Network error. Please try again.');
    } finally {
      setOperationLoading(prev => ({ ...prev, update: false }));
    }
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setPasswordData({
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswordModal(true);
    eventTracker.trackModalOpen('reset_password', { userId: user.userId });
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    if (!passwordData.newPassword || !passwordData.confirmPassword) {
      alert('Please fill in both password fields');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    setOperationLoading(prev => ({ ...prev, resetPassword: true }));

    try {
      const response = await fetch(RESET_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: selectedUser.userId,
          newPassword: passwordData.newPassword,
          adminEmail: currentUser?.email || 'admin'
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        eventTracker.trackUserAction('reset_password', selectedUser);
        alert('Password reset successfully!');
        setShowPasswordModal(false);
        setSelectedUser(null);
        setPasswordData({
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        alert(data.error || 'Failed to reset password');
      }

    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Network error. Failed to reset password.');
    } finally {
      setOperationLoading(prev => ({ ...prev, resetPassword: false }));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      setOperationLoading(prev => ({ ...prev, delete: true }));
      setLoadingUserId(userId);

      try {
        const response = await fetch(DELETEUSER_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: userId,
            deletedBy: currentUser?.email || 'admin',
            deleteMethod: 'soft'
          })
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setUsers(users.map(user =>
            user.userId === userId ? { ...user, status: 'deleted' } : user
          ));
          eventTracker.trackUserAction('delete_user', { userId });
          alert('User deleted successfully!');
        } else {
          alert(data.error || 'Failed to delete user');
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Network error. Failed to delete user.');
      } finally {
        setOperationLoading(prev => ({ ...prev, delete: false }));
        setLoadingUserId(null);
      }
    }
  };

  const handleReactivateUser = async (userId) => {
    if (window.confirm('Are you sure you want to reactivate this user?')) {
      setOperationLoading(prev => ({ ...prev, reactivate: true }));
      setLoadingUserId(userId);

      try {
        const user = users.find(u => u.userId === userId);
        const response = await fetch(UPDATEUSERS_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: userId,
            updateType: 'details',
            firstName: user.firstName,
            lastName: user.lastName,
            department: user.department || '',
            role: user.role || 'Negotiation Employee',
            status: 'active',
            updatedBy: currentUser?.email || 'admin',
            reactivatedAt: new Date().toISOString()
          })
        });

        const data = await response.json();

        if (data.success) {
          setUsers(users.map(u =>
            u.userId === userId
              ? { ...u, status: 'active' }
              : u
          ));
          eventTracker.trackUserAction('reactivate_user', { userId });
          alert('User reactivated successfully!');
        } else {
          alert(data.error || 'Failed to reactivate user');
        }
      } catch (error) {
        console.error('Error reactivating user:', error);
        alert('Network error. Failed to reactivate user.');
      } finally {
        setOperationLoading(prev => ({ ...prev, reactivate: false }));
        setLoadingUserId(null);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="user-management-wrapper">
        <div className="user-management-container">
          <div className="loading-spinner">
            <img src={loadingSpinner} alt="Loading..." className="loading-svg" />
            <p>Loading users...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-management-wrapper">
        <div className="user-management-container">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management-wrapper">
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          .tabs-container {
            display: flex;
            border-bottom: 2px solid #e5e7eb;
            margin-bottom: 24px;
            background-color: #f9fafb;
            padding: 0 24px;
          }
          
          .tab-button {
            padding: 12px 24px;
            background: none;
            border: none;
            cursor: pointer;
            font-size: 16px;
            font-weight: 500;
            color: #6b7280;
            position: relative;
            transition: color 0.2s;
          }
          
          .tab-button:hover {
            color: #374151;
          }
          
          .tab-button.active {
            color: #0066CC;
          }
          
          .tab-button.active::after {
            content: '';
            position: absolute;
            bottom: -2px;
            left: 0;
            right: 0;
            height: 2px;
            background-color: #0066CC;
          }
        `}
      </style>

      <div className="user-management-container">
        <div className="user-management-header">
          <h1>User Management and User Event Logs</h1>
          <p>Manage User ( Create Account, Roles, Permissions ) and User Event Activity Logs </p>
        </div>

        <div className="user-management-stats">
          <div className="stat-card">
            <h3>{users.length}</h3>
            <p>Total Users</p>
          </div>
          <div className="stat-card">
            <h3>{users.filter(u => u && u.status === 'active').length}</h3>
            <p>Active Users</p>
          </div>
          <div className="stat-card">
            <h3>{users.filter(u => u && u.role === 'Super Admin' && u.status !== 'deleted').length}</h3>
            <p>Super Admins</p>
          </div>
          <div className="stat-card">
            <h3>{users.filter(u => u && u.status === 'deleted').length}</h3>
            <p>Deleted Users</p>
          </div>
        </div>

        <div  className="user-container" >
            {/* Tabs */}
            <div className="tabs-container">
              <button
                className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('users');
                  eventTracker.trackButtonClick('switch_to_users_tab');
                }}
              >
                User Management
              </button>
              <button
                className={`tab-button ${activeTab === 'events' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('events');
                  eventTracker.trackButtonClick('switch_to_events_tab');
                }}
              >
                Event Logs
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'users' ? (
              <>
              <div className="user-management-footer">
                  <label className='title-header'>User List</label>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowCreateModal(true);
                      eventTracker.trackButtonClick('open_create_user_modal');
                    }}
                  >
                    Add New User
                  </button>

                  <button
                    onClick={() => {
                      setShowExportModal(true);
                      eventTracker.trackModalOpen('export_data');
                    }}
                    className="btn btn-secondary"
                    title="Export users data"
                  >
                    Export Data
                  </button>
                </div>
                <div className="filters-section">
                  <div className="filters-row">
                    <div className="search-box">
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                          clearTimeout(window.searchTimeout);
                          window.searchTimeout = setTimeout(() => {
                            if (e.target.value) {
                              eventTracker.trackSearch(e.target.value, filteredAndSortedUsers.length);
                            }
                          }, 500);
                        }}
                        className="search-input"
                      />
                    </div>

                    <div className="filter-group">
                      <label>Department:</label>
                      <select
                        value={departmentFilter}
                        onChange={(e) => {
                          setDepartmentFilter(e.target.value);
                          setCurrentPage(1);
                          eventTracker.trackFilter('department', e.target.value);
                        }}
                        className="filter-select"
                      >
                        {departments.map(dept => (
                          <option key={dept} value={dept}>
                            {dept === 'all' ? 'All Departments' : dept}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="filter-group">
                      <label>Role:</label>
                      <select
                        value={roleFilter}
                        onChange={(e) => {
                          setRoleFilter(e.target.value);
                          setCurrentPage(1);
                          eventTracker.trackFilter('role', e.target.value);
                        }}
                        className="filter-select"
                      >
                        {roles.map(role => (
                          <option key={role} value={role}>
                            {role === 'all' ? 'All Roles' : role}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="filter-group">
                      <label>Created From:</label>
                      <input
                        type="date"
                        value={dateRangeFilter.startDate}
                        onChange={(e) => {
                          setDateRangeFilter(prev => ({ ...prev, startDate: e.target.value }));
                          setCurrentPage(1);
                        }}
                        className="date-input"
                      />
                    </div>

                    <div className="filter-group">
                      <label>Created To:</label>
                      <input
                        type="date"
                        value={dateRangeFilter.endDate}
                        onChange={(e) => {
                          setDateRangeFilter(prev => ({ ...prev, endDate: e.target.value }));
                          setCurrentPage(1);
                        }}
                        className="date-input"
                      />
                    </div>

                    <button
                      onClick={handleResetFilters}
                      className="btn-reset-filters"
                      title="Reset all filters"
                    >
                      Reset
                    </button>
                  </div>


                </div>

                

             
                <div className="user-table-container">
                  <div className="table-scroll-wrapper">
                    <table className="user-table">
                      <thead>
                        <tr>
                          <th
                            style={{ width: '22%', cursor: 'pointer' }}
                            onClick={() => handleSort('name')}
                          >
                            User {sortConfig.key === 'name' && (
                              <span className="sort-indicator">
                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                          <th
                            style={{ width: '18%', cursor: 'pointer' }}
                            onClick={() => handleSort('email')}
                          >
                            Email {sortConfig.key === 'email' && (
                              <span className="sort-indicator">
                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                          <th
                            style={{ width: '10%', cursor: 'pointer' }}
                            onClick={() => handleSort('department')}
                          >
                            Department {sortConfig.key === 'department' && (
                              <span className="sort-indicator">
                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                          <th
                            style={{ width: '15%', cursor: 'pointer' }}
                            onClick={() => handleSort('role')}
                          >
                            Role {sortConfig.key === 'role' && (
                              <span className="sort-indicator">
                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                          <th
                            style={{ width: '10%', cursor: 'pointer' }}
                            onClick={() => handleSort('status')}
                          >
                            Status {sortConfig.key === 'status' && (
                              <span className="sort-indicator">
                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                          <th
                            style={{ width: '10%', cursor: 'pointer' }}
                            onClick={() => handleSort('createdAt')}
                          >
                            Created {sortConfig.key === 'createdAt' && (
                              <span className="sort-indicator">
                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                          <th
                            style={{ width: '10%', cursor: 'pointer' }}
                            onClick={() => handleSort('lastLogin')}
                          >
                            Last Login {sortConfig.key === 'lastLogin' && (
                              <span className="sort-indicator">
                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                          <th style={{ width: '15%', minWidth: '200px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedUsers.length === 0 ? (
                          <tr>
                            <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
                              No users found matching the filters
                            </td>
                          </tr>
                        ) : (
                          paginatedUsers.map((user) => {
                            if (!user || !user.userId) {
                              return null;
                            }

                            return (
                              <tr key={user.userId}>
                                <td>
                                  <div className="user-info">
                                    <div className="user-avatar">
                                      {user.firstName?.[0] || '?'}{user.lastName?.[0] || '?'}
                                    </div>
                                    <div>
                                      <div className="user-name">
                                        {user.firstName || 'Unknown'} {user.lastName || 'User'}
                                      </div>
                                      <div className="user-id">{user.userId}</div>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="user-email">{user.email || 'No email'}</div>
                                </td>
                                <td>{user.department || '-'}</td>
                                <td>
                                  <span className={`role-badge ${user.role?.replace(/\s+/g, '-').toLowerCase()}`}>
                                    {user.role || 'Negotiation Employee'}
                                  </span>
                                </td>
                                <td>
                                  <span className={`status-badge ${user.status || 'active'}`}>
                                    {user.status || 'active'}
                                  </span>
                                </td>
                                <td>
                                  <div className="date-cell">
                                    {user.createdAt && !isNaN(new Date(user.createdAt).getTime())
                                      ? new Date(user.createdAt).toLocaleDateString()
                                      : '-'}
                                  </div>
                                </td>
                                <td>
                                  <div className="date-cell">
                                    {user.lastLogin && !isNaN(new Date(user.lastLogin).getTime())
                                      ? new Date(user.lastLogin).toLocaleDateString()
                                      : '-'}
                                  </div>
                                </td>
                                <td>
                                  <div className="action-buttons">
                                    {user.status !== 'deleted' ? (
                                      <>
                                        <button
                                          className="btn-edit"
                                          onClick={() => handleEditUser(user)}
                                          title="Edit user"
                                        >
                                          Edit
                                        </button>
                                        <button
                                          className="btn-reset"
                                          onClick={() => handleResetPassword(user)}
                                          title="Reset password"
                                        >
                                          Reset PWD
                                        </button>
                                        {user.userId !== currentUser?.userId && (
                                          <button
                                            className="btn-delete"
                                            onClick={() => handleDeleteUser(user.userId)}
                                            disabled={operationLoading.delete && loadingUserId === user.userId}
                                            title="Delete user"
                                            style={{
                                              position: 'relative',
                                              minWidth: operationLoading.delete && loadingUserId === user.userId ? '80px' : 'auto'
                                            }}
                                          >
                                            {operationLoading.delete && loadingUserId === user.userId ? (
                                              <img
                                                src={loadingSpinner}
                                                alt="Loading..."
                                                style={{
                                                  width: '16px',
                                                  height: '16px',
                                                  animation: 'spin 1s linear infinite'
                                                }}
                                              />
                                            ) : (
                                              'Delete'
                                            )}
                                          </button>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          className="btn-reactivate"
                                          onClick={() => handleReactivateUser(user.userId)}
                                          disabled={operationLoading.reactivate && loadingUserId === user.userId}
                                          title="Reactivate user"
                                          style={{
                                            backgroundColor: operationLoading.reactivate && loadingUserId === user.userId ? '#6b7280' : '#10b981',
                                            color: 'white',
                                            border: 'none',
                                            padding: '4px 4px',
                                            height:'26px',
                                            borderRadius: '6px',
                                            cursor: operationLoading.reactivate && loadingUserId === user.userId ? 'not-allowed' : 'pointer',
                                            fontSize: '12px',
                                            position: 'relative',
                                            minWidth: operationLoading.reactivate && loadingUserId === user.userId ? '100px' : 'auto'
                                          }}
                                        >
                                          {operationLoading.reactivate && loadingUserId === user.userId ? (
                                            <img
                                              src={loadingSpinner}
                                              alt="Loading..."
                                              style={{
                                                width: '16px',
                                                height: '16px',
                                                animation: 'spin 1s linear infinite'
                                              }}
                                            />
                                          ) : (
                                            'Reactivate'
                                          )}
                                        </button>
                                        <span className="deleted-label" style={{ marginLeft: '10px' }}>Deleted</span>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="table-footer-actions" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderTop: '1px solid #e5e7eb',
                    backgroundColor: '#f9fafb'
                  }}>
                    <div className="results-info" style={{
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>
                      Showing {paginatedUsers.length} of {filteredAndSortedUsers.length} users
                    </div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px'
                    }}>
                      <div className="rows-per-page" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <label style={{
                          fontSize: '14px',
                          color: '#6b7280'
                        }}>Rows per page:</label>
                        <select
                          value={rowsPerPage}
                          onChange={(e) => {
                            setRowsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                          className="rows-select"
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '14px',
                            backgroundColor: 'white'
                          }}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={30}>30</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>

                      
                    </div>
                  </div>
                </div>

                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      Previous
                    </button>

                    <div className="page-numbers">
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNum = index + 1;
                        if (
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`page-num ${currentPage === pageNum ? 'active' : ''}`}
                            >
                              {pageNum}
                            </button>
                          );
                        } else if (
                          pageNum === currentPage - 2 ||
                          pageNum === currentPage + 2
                        ) {
                          return <span key={pageNum} className="page-dots">...</span>;
                        }
                        return null;
                      })}
                    </div>

                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <EventLogs />
            )}
        </div>

        {showExportModal && (
          <div className="modal-overlay">
            <div className="modal export-modal">
              <div className="modal-header">
                <h2>Export User Data</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowExportModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>Choose export format for {filteredAndSortedUsers.length} users:</p>
                <div className="export-options">
                  <button
                    onClick={() => handleExportUsers('csv')}
                    className="btn-export-option"
                  >
                    <span className="export-icon">📄</span>
                    Export as CSV
                  </button>
                  <button
                    onClick={() => handleExportUsers('json')}
                    className="btn-export-option"
                  >
                    <span className="export-icon">📋</span>
                    Export as JSON
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showCreateModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>Create New User</h2>
                <button
                  className="modal-close"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEmailError('');
                    setFormData({
                      email: '',
                      password: '',
                      firstName: '',
                      lastName: '',
                      department: '',
                      role: 'Negotiation Employee',
                      status: 'active'
                    });
                  }}
                  disabled={operationLoading.create}
                  style={{ opacity: operationLoading.create ? 0.5 : 1, cursor: operationLoading.create ? 'not-allowed' : 'pointer' }}
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleCreateUser} className="modal-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter first name"
                      required
                    />
                    <small>Letters only, no numbers or special characters</small>
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter last name"
                      required
                    />
                    <small>Letters only, no numbers or special characters</small>
                  </div>
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="username@pacificdebt.com"
                    pattern="[a-z0-9._%+-]+@pacificdebt\.com$"
                    title="Email must be a @pacificdebt.com address"
                    className={emailError ? 'error' : ''}
                    required
                  />
                  <small className={emailError ? 'error-text' : ''}>
                    {emailError || 'Must be a @pacificdebt.com email address'}
                  </small>
                </div>
                <div className="form-group">
                  <label>Department</label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Enter department"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Role *</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="Negotiation Employee">Negotiation Employee</option>
                      <option value="Negotiation Supervisor">Negotiation Supervisor</option>
                      <option value="Client Success Employee">Client Success Employee</option>
                      <option value="Client Success Supervisor">Client Success Supervisor</option>
                      <option value="Client Care Employee">Client Care Employee</option>
                      <option value="Client Care Supervisor">Client Care Supervisor</option>
                      <option value="Super Admin">Super Admin</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Initial Password *</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      minLength="6"
                    />
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-cancel" onClick={() => {
                    setShowCreateModal(false);
                    setEmailError('');
                    setFormData({
                      email: '',
                      password: '',
                      firstName: '',
                      lastName: '',
                      department: '',
                      role: 'Negotiation Employee',
                      status: 'active'
                    });
                  }} disabled={operationLoading.create}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={operationLoading.create}>
                    {operationLoading.create ? (
                      <img
                        src={loadingSpinner}
                        alt="Loading..."
                        style={{
                          width: '16px',
                          height: '16px',
                          animation: 'spin 1s linear infinite'
                        }}
                      />
                    ) : (
                      'Create User'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showEditModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>Edit User Information</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowEditModal(false)}
                  disabled={operationLoading.update}
                  style={{ opacity: operationLoading.update ? 0.5 : 1, cursor: operationLoading.update ? 'not-allowed' : 'pointer' }}
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleUpdateUser} className="modal-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter first name"
                      required
                    />
                    <small>Letters only, no numbers or special characters</small>
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter last name"
                      required
                    />
                    <small>Letters only, no numbers or special characters</small>
                  </div>
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    disabled
                    className="disabled-input"
                  />
                  <small>Email cannot be changed</small>
                </div>
                <div className="form-group">
                  <label>Department</label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Enter department"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Role</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      disabled={selectedUser?.userId === currentUser?.userId}
                    >
                      <option value="Negotiation Employee">Negotiation Employee</option>
                      <option value="Negotiation Supervisor">Negotiation Supervisor</option>
                      <option value="Client Success Employee">Client Success Employee</option>
                      <option value="Client Success Supervisor">Client Success Supervisor</option>
                      <option value="Client Care Employee">Client Care Employee</option>
                      <option value="Client Care Supervisor">Client Care Supervisor</option>
                      <option value="Super Admin">Super Admin</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      disabled={selectedUser?.userId === currentUser?.userId}
                    >
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)} disabled={operationLoading.update}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={operationLoading.update}>
                    {operationLoading.update ? (
                      <img
                        src={loadingSpinner}
                        alt="Loading..."
                        style={{
                          width: '16px',
                          height: '16px',
                          animation: 'spin 1s linear infinite'
                        }}
                      />
                    ) : (
                      'Update User'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showPasswordModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>Reset Password</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowPasswordModal(false)}
                  disabled={operationLoading.resetPassword}
                  style={{ opacity: operationLoading.resetPassword ? 0.5 : 1, cursor: operationLoading.resetPassword ? 'not-allowed' : 'pointer' }}
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleUpdatePassword} className="modal-form">
                <div className="form-group">
                  <label>User</label>
                  <input
                    type="text"
                    value={`${selectedUser?.firstName} ${selectedUser?.lastName} (${selectedUser?.email})`}
                    disabled
                    className="disabled-input"
                  />
                </div>
                <div className="form-group">
                  <label>New Password *</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                    minLength="6"
                  />
                </div>
                <div className="form-group">
                  <label>Confirm New Password *</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                    minLength="6"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-cancel" onClick={() => setShowPasswordModal(false)} disabled={operationLoading.resetPassword}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={operationLoading.resetPassword}>
                    {operationLoading.resetPassword ? (
                      <img
                        src={loadingSpinner}
                        alt="Loading..."
                        style={{
                          width: '16px',
                          height: '16px',
                          animation: 'spin 1s linear infinite'
                        }}
                      />
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {(operationLoading.create || operationLoading.update || operationLoading.delete ||
        operationLoading.reactivate || operationLoading.resetPassword) && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px 40px',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <img
                src={loadingSpinner}
                alt="Loading..."
                style={{
                  width: '48px',
                  height: '48px',
                  animation: 'spin 1s linear infinite'
                }}
              />
              <div style={{ textAlign: 'center' }}>
                <p style={{ margin: 0, fontWeight: 600, fontSize: '18px', color: '#1f2937' }}>
                  Processing
                </p>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                  {operationLoading.create && 'Creating new user...'}
                  {operationLoading.update && 'Updating user information...'}
                  {operationLoading.delete && 'Deleting user...'}
                  {operationLoading.reactivate && 'Reactivating user account...'}
                  {operationLoading.resetPassword && 'Resetting user password...'}
                </p>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default UserManagement;